/**
 * "Export report" on a campaign or plan definition (issue #119).
 *
 * A small menu of Summary/Full x HTML/CSV. Both formats come from one
 * structure on the server, so the choice is only how much and which shape.
 * Downloaded through `downloadGet`, so the file carries the program the picker
 * shows rather than whichever one the server last guessed.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { api } from '../../api'
import type { VerificationReportDetail, VerificationReportFormat } from '../../api'

/** The four choices, in the order the menu shows them. */
export const REPORT_CHOICES: ReadonlyArray<{
  label: string
  detail: VerificationReportDetail
  format: VerificationReportFormat
}> = [
  { label: 'Summary (HTML)', detail: 'summary', format: 'html' },
  { label: 'Summary (CSV)', detail: 'summary', format: 'csv' },
  { label: 'Full (HTML)', detail: 'full', format: 'html' },
  { label: 'Full (CSV)', detail: 'full', format: 'csv' },
]

export default function VerificationReportMenu({ identifier }: { identifier: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [error, setError] = useState('')

  const choose = async (choice: (typeof REPORT_CHOICES)[number]) => {
    setAnchor(null)
    setError('')
    try {
      await api.downloadVerificationReport(identifier, choice.format, choice.detail)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    }
  }

  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start', mb: 1 }}>
      <Button variant="outlined" size="small" onClick={(event) => setAnchor(event.currentTarget)}>
        Export report
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {REPORT_CHOICES.map((choice) => (
          <MenuItem key={choice.label} onClick={() => void choose(choice)}>
            {choice.label}
          </MenuItem>
        ))}
      </Menu>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  )
}
